import { computed, watch, type ComputedRef, type Ref, type WritableComputedRef } from 'vue'
import type { PDataTableRow, PDataTableRowIdValue, PDataTableRowKey } from '../types'
import type { TQDataTableBody } from '../qTypes'

type UseSelectionOptions<T extends PDataTableRow> = {
  items: Ref<readonly T[]>
  rowKey: Ref<PDataTableRowKey>
  expandableItemsKey: Ref<PDataTableRowKey | null>
  selectable: Ref<boolean>
  selectableChildren: Ref<boolean>
  selectedItems: Ref<PDataTableRowIdValue[]>
  selectedChildItems: Ref<PDataTableRowIdValue[]>
  isRowSelectable?: Ref<((row: T) => boolean) | null>
}

export type UseSelectionReturn<T extends PDataTableRow> = {
  selectedRows: WritableComputedRef<T[]>
  headerCheckboxValue: ComputedRef<boolean | null>
  isSelectable: (row: T) => boolean
  parentCheckboxValue: (scope: TQDataTableBody<T>) => boolean | null
  isChildSelected: (childId: PDataTableRowIdValue) => boolean
  setChildSelected: (childId: PDataTableRowIdValue, selected: boolean | null) => void
  onHeaderCheckboxToggle: (value: boolean | null) => void
  onParentCheckboxToggle: (scope: TQDataTableBody<T>, value: boolean | null) => void
}

export const useSelection = <T extends PDataTableRow>({
  items,
  rowKey,
  expandableItemsKey,
  selectable,
  selectableChildren,
  selectedItems,
  selectedChildItems,
  isRowSelectable,
}: UseSelectionOptions<T>): UseSelectionReturn<T> => {
  const isSelectable = (row: T): boolean => {
    const predicate = isRowSelectable?.value ?? null

    return predicate === null ? true : predicate(row)
  }

  const selectableItems = computed<T[]>(() => items.value.filter((row) => isSelectable(row)))

  const childIdsByParent = computed<Map<PDataTableRowIdValue, PDataTableRowIdValue[]>>(() => {
    const map = new Map<PDataTableRowIdValue, PDataTableRowIdValue[]>()

    if (!expandableItemsKey.value) {
      return map
    }

    const childKey = expandableItemsKey.value

    for (const parent of items.value) {
      const parentId = parent[rowKey.value] as PDataTableRowIdValue
      const children = (parent[childKey] ?? []) as T[]
      const childIds = children.map((child) => child[rowKey.value] as PDataTableRowIdValue)
      map.set(parentId, childIds)
    }

    return map
  })

  const parentIdByChild = computed<Map<PDataTableRowIdValue, PDataTableRowIdValue>>(() => {
    const map = new Map<PDataTableRowIdValue, PDataTableRowIdValue>()

    for (const [parentId, childIds] of childIdsByParent.value) {
      for (const childId of childIds) {
        map.set(childId, parentId)
      }
    }

    return map
  })

  const selectedItemsSet = computed<Set<PDataTableRowIdValue>>(() => new Set(selectedItems.value))
  const selectedChildItemsSet = computed<Set<PDataTableRowIdValue>>(
    () => new Set(selectedChildItems.value),
  )

  const childIdsOfParents = (parentIds: PDataTableRowIdValue[]): PDataTableRowIdValue[] => {
    const result: PDataTableRowIdValue[] = []

    for (const id of parentIds) {
      const childIds = childIdsByParent.value.get(id)

      if (childIds === undefined) {
        continue
      }

      result.push(...childIds)
    }

    return result
  }

  const findParentIdOfChild = (childId: PDataTableRowIdValue): PDataTableRowIdValue | null => {
    const parentId = parentIdByChild.value.get(childId)

    if (parentId === undefined) {
      return null
    }

    return parentId
  }

  const isChildSelected = (childId: PDataTableRowIdValue): boolean => {
    if (selectedChildItemsSet.value.has(childId)) {
      return true
    }

    const parentId = findParentIdOfChild(childId)

    if (parentId === null) {
      return false
    }

    return selectedItemsSet.value.has(parentId)
  }

  const computeParentCheckboxValue = (rowId: PDataTableRowIdValue): boolean | null => {
    if (selectedItemsSet.value.has(rowId)) {
      return true
    }

    if (!selectableChildren.value || !expandableItemsKey.value) {
      return false
    }

    const childIds = childIdsByParent.value.get(rowId) ?? []

    if (childIds.length === 0) {
      return false
    }

    let selectedCount = 0

    for (const id of childIds) {
      if (selectedChildItemsSet.value.has(id)) {
        selectedCount++
      }
    }

    if (selectedCount === 0) {
      return false
    }

    if (selectedCount === childIds.length) {
      return true
    }

    return null
  }

  const parentCheckboxValue = (scope: TQDataTableBody<T>): boolean | null => {
    return computeParentCheckboxValue(scope.row[rowKey.value] as PDataTableRowIdValue)
  }

  const headerCheckboxValue = computed<boolean | null>(() => {
    if (!selectable.value || selectableItems.value.length === 0) {
      return false
    }

    let allTrue = true
    let anyNonFalse = false

    for (const row of selectableItems.value) {
      const state = computeParentCheckboxValue(row[rowKey.value] as PDataTableRowIdValue)

      if (state === true) {
        anyNonFalse = true
      } else if (state === null) {
        anyNonFalse = true
        allTrue = false
      } else {
        allTrue = false
      }
    }

    if (allTrue && anyNonFalse) {
      return true
    }

    if (anyNonFalse) {
      return null
    }

    return false
  })

  const selectedRows = computed<T[]>({
    get() {
      return items.value.filter((row) =>
        selectedItemsSet.value.has(row[rowKey.value] as PDataTableRowIdValue),
      )
    },
    set(value: T[]) {
      const newSelectedItems = value.map((row) => row[rowKey.value] as PDataTableRowIdValue)
      selectedItems.value = newSelectedItems

      if (!selectableChildren.value || !expandableItemsKey.value) {
        return
      }

      const newChildIds = new Set(childIdsOfParents(newSelectedItems))

      if (newChildIds.size === 0) {
        return
      }

      const cleanedChildren = selectedChildItems.value.filter((id) => !newChildIds.has(id))

      if (cleanedChildren.length !== selectedChildItems.value.length) {
        selectedChildItems.value = cleanedChildren
      }
    },
  })

  const setChildSelected = (childId: PDataTableRowIdValue, selected: boolean | null): void => {
    const parentId = findParentIdOfChild(childId)
    const parentIsFullySelected = parentId !== null && selectedItemsSet.value.has(parentId)

    if (selected === true) {
      if (parentIsFullySelected) {
        return
      }

      const nextChildren = selectedChildItemsSet.value.has(childId)
        ? selectedChildItems.value
        : [...selectedChildItems.value, childId]

      selectedChildItems.value = nextChildren

      if (parentId === null) {
        return
      }

      const siblingIds = childIdsOfParents([parentId])
      const nextChildrenSet = new Set(nextChildren)
      const allSiblingsSelected =
        siblingIds.length > 0 && siblingIds.every((id) => nextChildrenSet.has(id))

      if (!allSiblingsSelected) {
        return
      }

      const siblingSet = new Set(siblingIds)
      selectedChildItems.value = nextChildren.filter((id) => !siblingSet.has(id))
      selectedItems.value = [...selectedItems.value, parentId]
      return
    }

    if (parentIsFullySelected) {
      const siblingIds = childIdsOfParents([parentId!])
      const existingChildren = selectedChildItemsSet.value
      const otherSiblings = siblingIds.filter((id) => id !== childId && !existingChildren.has(id))

      selectedItems.value = selectedItems.value.filter((id) => id !== parentId)
      selectedChildItems.value = [...selectedChildItems.value, ...otherSiblings]
      return
    }

    selectedChildItems.value = selectedChildItems.value.filter((id) => id !== childId)
  }

  const onHeaderCheckboxToggle = (value: boolean | null): void => {
    if (value === true) {
      const allParentIds = selectableItems.value.map(
        (row) => row[rowKey.value] as PDataTableRowIdValue,
      )
      selectedItems.value = allParentIds

      if (!selectableChildren.value || !expandableItemsKey.value) {
        return
      }

      const allChildIds = new Set(childIdsOfParents(allParentIds))

      if (allChildIds.size === 0) {
        return
      }

      const cleaned = selectedChildItems.value.filter((id) => !allChildIds.has(id))

      if (cleaned.length !== selectedChildItems.value.length) {
        selectedChildItems.value = cleaned
      }

      return
    }

    if (selectedItems.value.length > 0) {
      selectedItems.value = []
    }

    if (selectedChildItems.value.length > 0) {
      selectedChildItems.value = []
    }
  }

  const onParentCheckboxToggle = (scope: TQDataTableBody<T>, value: boolean | null): void => {
    const rowId = scope.row[rowKey.value] as PDataTableRowIdValue
    const select = value === true

    if (select && !isSelectable(scope.row)) {
      return
    }

    if (select && !selectedItemsSet.value.has(rowId)) {
      selectedItems.value = [...selectedItems.value, rowId]
    } else if (!select && selectedItemsSet.value.has(rowId)) {
      selectedItems.value = selectedItems.value.filter((id) => id !== rowId)
    }

    if (!selectableChildren.value || !expandableItemsKey.value) {
      return
    }

    const childIds = new Set(childIdsByParent.value.get(rowId) ?? [])

    if (childIds.size === 0) {
      return
    }

    const cleaned = selectedChildItems.value.filter((id) => !childIds.has(id))

    if (cleaned.length !== selectedChildItems.value.length) {
      selectedChildItems.value = cleaned
    }
  }

  // Normalize pre-populated / externally-set selection: if every child of some
  // parent is in selectedChildItems, promote those children to a single entry in
  // selectedItems. Keeps the two paths to "fully selected parent" (explicit
  // programmatic prefill vs. click-through promote) in the same canonical shape.
  watch(
    [selectedChildItems, items],
    () => {
      if (!selectableChildren.value || !expandableItemsKey.value) {
        return
      }

      if (selectedChildItems.value.length === 0) {
        return
      }

      const selectedChildSet = selectedChildItemsSet.value
      const parentsToAdd: PDataTableRowIdValue[] = []
      const childrenToRemove = new Set<PDataTableRowIdValue>()

      for (const [parentId, childIds] of childIdsByParent.value) {
        if (selectedItemsSet.value.has(parentId)) {
          continue
        }

        if (childIds.length === 0) {
          continue
        }

        const allSelected = childIds.every((id) => selectedChildSet.has(id))

        if (!allSelected) {
          continue
        }

        parentsToAdd.push(parentId)
        childIds.forEach((id) => childrenToRemove.add(id))
      }

      if (parentsToAdd.length === 0) {
        return
      }

      selectedItems.value = [...selectedItems.value, ...parentsToAdd]
      selectedChildItems.value = selectedChildItems.value.filter((id) => !childrenToRemove.has(id))
    },
    { immediate: true },
  )

  return {
    selectedRows,
    headerCheckboxValue,
    isSelectable,
    parentCheckboxValue,
    isChildSelected,
    setChildSelected,
    onHeaderCheckboxToggle,
    onParentCheckboxToggle,
  }
}
